// CaseReport.js - Updated with production URLs
import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { useSelector } from "react-redux";

const CaseReport = () => {
  const pid = useSelector((state) => state.user.projectId);
  const userEmail = useSelector((state) => state.user.email);

  // Filter states
  const [orgOptions, setOrgOptions] = useState([]);
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  const [caseDistrictOptions, setCaseDistrictOptions] = useState([]);
  const [hdidOptions, setHdidOptions] = useState([]);
  const [hdNameOptions, setHdNameOptions] = useState([]);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [selectedCaseDistrict, setSelectedCaseDistrict] = useState([]);
  const [selectedHDID, setSelectedHDID] = useState([]);
  const [selectedHDNames, setSelectedHDNames] = useState([]);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const schemeTypeLabelMap = {
    sch: "Schemes",
    doc: "Document",
    dfl: "DFL",
    yc: "Yojana Card"
  };

  // Base URLs - Update these for production
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api-project360-insights.haqdarshak.com'; // Update with your domain
  // For local development:
  // const API_BASE_URL = 'https://api-project360-insights.haqdarshak.com';
  
  const WATERFALL_BASE_URL = `${API_BASE_URL}/kpi/waterfall`;
  const HD_BASE_URL = `${API_BASE_URL}/kpi/hd`;

  const allColumns = [
    { key: 'pid', label: 'Project ID' },
    { key: 'ProjectName', label: 'Project Name' },
    { key: 'State', label: 'State' },
    { key: 'Organization_Name', label: 'Organization Name' },
    { key: 'OrgID', label: 'Organization ID' },
    { key: 'CaseID', label: 'Case ID' },
    { key: 'CaseDistrict', label: 'Case District' },
    { key: 'CitizenGUID', label: 'Citizen GUID' },
    { key: 'Age', label: 'Age' },
    { key: 'CitizenState', label: 'Citizen State' },
    { key: 'CitizenDistrict', label: 'Citizen District' },
    { key: 'CitizenGender', label: 'Citizen Gender' },
    { key: 'HDID', label: 'HD ID' },
    // { key: 'haqdarshakName', label: 'Haqdarshak Name' },
    { key: 'haqdarshakState', label: 'Haqdarshak State' },
    { key: 'haqdarshakDistrict', label: 'Haqdarshak District' },
    { key: 'ration_card_type', label: 'Ration Card Type' },
    { key: 'target_group', label: 'Target Group' },
    { key: 'area_type', label: 'Area Type' },
    { key: 'Caste', label: 'Caste' },
    { key: 'Income', label: 'Income' },
    { key: 'Tribal_Status', label: 'Tribal Status' },
    { key: 'PWD_Status', label: 'PWD Status' },
    { key: 'SchemeGUID', label: 'Scheme GUID' },
    { key: 'enterprise_id', label: 'Enterprise ID' },
    { key: 'SchemeName', label: 'Scheme Name' },
    { key: 'SchemeType', label: 'Scheme Type' },
    { key: 'SchemeValue', label: 'Scheme Value' }
  ];

  const mandatoryColumns = [
    { key: 'created_on', label: 'Created On' },
    { key: 'ds_date', label: 'DS Date' },
    { key: 'br_date', label: 'BR Date' },
    { key: 'case_status', label: 'Case Status' }
  ];

  const buildOptions = (fetchedValues, selectedValues) => {
    const fetched = fetchedValues?.map((v) => ({ value: v, label: v })) || [];
    const selectedOnly = selectedValues
      .filter((v) => !fetchedValues?.includes(v))
      .map((v) => ({ value: v, label: v }));
    return [...selectedOnly, ...fetched];
  };

  const getFilterQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    if (pid) params.append("pid", pid);
    selectedOrgs.forEach((o) => params.append("OrgName", o));
    selectedSchemes.forEach((s) => params.append("SchemeName", s));
    selectedSchemeTypes.forEach((t) => params.append("SchemeType", t));
    selectedCaseDistrict.forEach((d) => params.append("CaseDistrict", d));
    selectedHDID.forEach((h) => params.append("HDID", h));
    selectedHDNames.forEach((n) => params.append("haqdarshakName", n));
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    return params.toString();
  }, [pid, selectedOrgs, selectedSchemes, selectedSchemeTypes, selectedCaseDistrict, selectedHDID, selectedHDNames, startDate, endDate]);

  const fetchFilterOptions = async () => {
    try {
      const params = getFilterQueryParams();
      
      const [orgRes, schemeRes, schemeTypeRes, caseDistrictRes] = await Promise.all([
        fetch(`${WATERFALL_BASE_URL}/unique-org/?${params}`),
        fetch(`${WATERFALL_BASE_URL}/unique-scheme/?${params}`),
        fetch(`${WATERFALL_BASE_URL}/unique-scheme-type/?${params}`),
        fetch(`${WATERFALL_BASE_URL}/unique-case-districts/?${params}`)
      ]);

      const [hdidRes, hdnameRes] = await Promise.all([
        fetch(`${HD_BASE_URL}/unique-hd-id/?${params}`),
        fetch(`${HD_BASE_URL}/unique-hd-name/?${params}`)
      ]);

      const orgData = await orgRes.json();
      const schemeData = await schemeRes.json();
      const schemeTypeData = await schemeTypeRes.json();
      const caseDistrictData = await caseDistrictRes.json();
      const hdidData = await hdidRes.json();
      const hdnameData = await hdnameRes.json();

      setOrgOptions(buildOptions(orgData.unique_Organizations, selectedOrgs));
      setSchemeOptions(buildOptions(schemeData.unique_Schemes, selectedSchemes));
      
      const schemeTypeOpts = (schemeTypeData.unique_Scheme_Types || []).map(t => ({ 
        value: t, 
        label: schemeTypeLabelMap[t] || t
      }));
      setSchemeTypeOptions(schemeTypeOpts);
      
      setCaseDistrictOptions(buildOptions(caseDistrictData.unique_case_districts, selectedCaseDistrict));
      setHdidOptions(buildOptions(hdidData.unique_HDID || [], selectedHDID));
      setHdNameOptions(buildOptions(hdnameData.unique_HaqdarshakNames || [], selectedHDNames));

    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFilterOptions();
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedOrgs, selectedSchemes, selectedSchemeTypes, selectedCaseDistrict, selectedHDID, selectedHDNames, startDate, endDate, pid]);

  const logDownload = async (selectedColumnsList) => {
    try {
      const mandatoryColumnKeys = mandatoryColumns.map(col => col.key);
      const allDownloadItems = [...new Set([...mandatoryColumnKeys, ...selectedColumnsList])];
      
      const response = await fetch(`${API_BASE_URL}/kpi/case-report/log-download/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pid: pid,
          email: userEmail || 'anonymous@user.com',
          download_items: allDownloadItems
        })
      });
      
      const data = await response.json();
      if (data.success) {
        console.log('Download logged successfully at:', data.download_time);
      } else {
        console.error('Failed to log download:', data.error);
      }
    } catch (error) {
      console.error('Error logging download:', error);
    }
  };

  const handleDownloadCSV = async () => {
    if (selectedColumns.length === 0) {
      alert('Please select at least one column to download the report');
      return;
    }
    
    if ((startDate && !endDate) || (!startDate && endDate)) {
      setDateError("Both Start Date and End Date are required for date range filter");
      alert("Please select both Start Date and End Date for date range filter");
      return;
    }
    
    setDownloading(true);
    
    try {
      await logDownload(selectedColumns);
      
      const params = new URLSearchParams();
      
      selectedSchemeTypes.forEach(type => params.append("SchemeType", type));
      selectedOrgs.forEach(org => params.append("OrgName", org));
      selectedSchemes.forEach(scheme => params.append("SchemeName", scheme));
      selectedCaseDistrict.forEach(district => params.append("CaseDistrict", district));
      selectedHDID.forEach(hdid => params.append("HDID", hdid));
      selectedHDNames.forEach(name => params.append("haqdarshakName", name));
      
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (pid) params.append("pid", pid);
      
      selectedColumns.forEach(col => params.append("columns", col));
      
      const downloadUrl = `${API_BASE_URL}/kpi/case-report/generate/?${params.toString()}`;

      // Fetch via the interceptor (attaches the auth token), then download the
      // returned file client-side. A plain window.location navigation would NOT
      // carry the Authorization header and would be rejected.
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;

      // Prefer the server-provided filename, else a sensible default.
      const disposition = response.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      link.download = match ? match[1] : "case-report.csv";

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error during download process:', error);
      alert('There was an error downloading the report. Please try again.');
    } finally {
      setTimeout(() => setDownloading(false), 3000);
    }
  };

  const MultiSelectDropdown = ({ options, selected, setSelected, placeholder, isDisabled = false }) => (
    <Select
      isMulti
      options={options}
      value={options.filter(opt => selected.includes(opt.value))}
      onChange={(selectedOptions) => setSelected(selectedOptions.map(o => o.value))}
      placeholder={placeholder}
      className="w-full"
      closeMenuOnSelect={false}
      isDisabled={isDisabled}
    />
  );

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedSchemes([]);
    setSelectedSchemeTypes([]);
    setSelectedCaseDistrict([]);
    setSelectedHDID([]);
    setSelectedHDNames([]);
    setStartDate('');
    setEndDate('');
    setSelectedColumns([]);
    setDateError('');
  };

  const toggleAllColumns = () => {
    if (selectedColumns.length === allColumns.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(allColumns.map(col => col.key));
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  return (
    <div className="max-w-[90vw] ml-[4vw] mt-[3vh] border-2 border-gray-300 shadow-md rounded-md bg-white">
      
      <div className="bg-gray-50 px-4 md:px-6 py-4 border rounded-lg mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-pink-800">
          Case Report Generator
        </h2>
        <p className="text-sm text-pink-800 mt-1">
          Select filters and columns to download case report (CSV format)
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 mb-6">
        <h3 className="text-md font-semibold text-gray-700 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          
          <div>
            <label className="text-sm font-medium text-gray-700">Organization</label>
            <MultiSelectDropdown
              options={orgOptions}
              selected={selectedOrgs}
              setSelected={setSelectedOrgs}
              placeholder="Select organization..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Service Type</label>
            <MultiSelectDropdown
              options={schemeTypeOptions}
              selected={selectedSchemeTypes}
              setSelected={setSelectedSchemeTypes}
              placeholder="Select service type..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Scheme Name</label>
            <MultiSelectDropdown
              options={schemeOptions}
              selected={selectedSchemes}
              setSelected={setSelectedSchemes}
              placeholder="Select scheme..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Case District</label>
            <MultiSelectDropdown
              options={caseDistrictOptions}
              selected={selectedCaseDistrict}
              setSelected={setSelectedCaseDistrict}
              placeholder="Select district..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">HD ID</label>
            <MultiSelectDropdown
              options={hdidOptions}
              selected={selectedHDID}
              setSelected={setSelectedHDID}
              placeholder="Select HD ID..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Haqdarshak Name</label>
            <MultiSelectDropdown
              options={hdNameOptions}
              selected={selectedHDNames}
              setSelected={setSelectedHDNames}
              placeholder="Select Haqdarshak name..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (e.target.value && !endDate) {
                  setDateError("Please also select an End Date");
                } else if (!e.target.value && endDate) {
                  setDateError("Please also select a Start Date");
                } else {
                  setDateError("");
                }
              }}
              className={`w-full p-2 border ${dateError ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-pink-500 focus:border-pink-500`}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (startDate && !e.target.value) {
                  setDateError("Please also select an End Date");
                } else if (!startDate && e.target.value) {
                  setDateError("Please also select a Start Date");
                } else {
                  setDateError("");
                }
              }}
              min={startDate || undefined}
              className={`w-full p-2 border ${dateError ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-pink-500 focus:border-pink-500`}
            />
          </div>
        </div>

        {dateError && (
          <div className="mt-2 text-red-500 text-sm">
            ⚠ {dateError}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={clearFilters}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-md transition"
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-md font-semibold text-gray-700">Select Columns to Export</h3>
          <button
            onClick={toggleAllColumns}
            className="text-sm bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1 rounded-md transition"
          >
            {selectedColumns.length === allColumns.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-96 overflow-y-auto p-2 border border-gray-200 rounded-lg">
          {allColumns.map((column) => (
            <label key={column.key} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-md cursor-pointer">
              <input
                type="checkbox"
                checked={selectedColumns.includes(column.key)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedColumns([...selectedColumns, column.key]);
                  } else {
                    setSelectedColumns(selectedColumns.filter(c => c !== column.key));
                  }
                }}
                className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
              />
              <span className="text-sm text-gray-700">{column.label}</span>
            </label>
          ))}
        </div>
        
        <div className="mt-3 p-2 bg-blue-50 rounded-md">
          <p className="text-xs text-blue-700">
            <span className="font-semibold">Note:</span> The following columns will be automatically added to your report: 
            <strong> Created On, DS Date, BR Date, Case Status</strong> (one row per Case ID)
          </p>
        </div>

        <div className="mt-2 p-2 bg-gray-50 rounded-md">
          <p className="text-xs text-gray-600">
            Selected {selectedColumns.length} of {allColumns.length} columns + 4 mandatory columns automatically included
          </p>
        </div>
        
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleDownloadCSV}
            disabled={downloading || selectedColumns.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-md transition flex items-center gap-2 disabled:opacity-50 text-lg font-semibold"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {downloading ? 'Downloading...' : 'Download CSV Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaseReport;